import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEdit,
  FaKey,
  FaTrashAlt,
} from 'react-icons/fa';
import SectionHeader from '../components/SectionHeader';
import StudioItemForm from '../components/StudioItemForm';
import useSiteSeo from '../hooks/useSiteSeo';

const STUDIO_KEY_STORAGE = 'kyros-studio-key';

function createEmptyLink() {
  return {
    label: '',
    url: '',
    type: 'Demo',
  };
}

function createEmptyVideo() {
  return {
    title: '',
    url: '',
  };
}

function createEmptyItemForm(taxonomies) {
  return {
    title: '',
    collection: taxonomies?.collections?.[0] || 'Projects',
    category: taxonomies?.categories?.[0] || 'Web',
    status: taxonomies?.statuses?.[0] || 'In Progress',
    priceLabel: '',
    coverImageUrl: '',
    summary: '',
    impact: '',
    description: '',
    buildInstructions: '',
    availabilityNote: '',
    technologiesInput: '',
    platformsInput: '',
    tagsInput: '',
    featured: false,
    links: [createEmptyLink()],
    videos: [createEmptyVideo()],
  };
}

function itemToFormState(item) {
  return {
    title: item.title || '',
    collection: item.collection || 'Projects',
    category: item.category || 'Web',
    status: item.status || 'In Progress',
    priceLabel: item.priceLabel || '',
    coverImageUrl: item.coverImageUrl || '',
    summary: item.summary || '',
    impact: item.impact || '',
    description: item.description || '',
    buildInstructions: item.buildInstructions || '',
    availabilityNote: item.availabilityNote || '',
    technologiesInput: (item.technologies || []).join(', '),
    platformsInput: (item.platforms || []).join(', '),
    tagsInput: (item.tags || []).join(', '),
    featured: Boolean(item.featured),
    links: item.links?.length ? item.links : [createEmptyLink()],
    videos: item.videos?.length ? item.videos : [createEmptyVideo()],
  };
}

function splitCommaField(value) {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function formToPayload(itemForm) {
  return {
    title: itemForm.title,
    collection: itemForm.collection,
    category: itemForm.category,
    status: itemForm.status,
    priceLabel: itemForm.priceLabel,
    coverImageUrl: itemForm.coverImageUrl,
    summary: itemForm.summary,
    impact: itemForm.impact,
    description: itemForm.description,
    buildInstructions: itemForm.buildInstructions,
    availabilityNote: itemForm.availabilityNote,
    technologies: splitCommaField(itemForm.technologiesInput),
    platforms: splitCommaField(itemForm.platformsInput),
    tags: splitCommaField(itemForm.tagsInput),
    featured: itemForm.featured,
    links: itemForm.links,
    videos: itemForm.videos,
  };
}

function buildProfileForm(profile) {
  return {
    name: profile?.name || '',
    alternateNamesInput: (profile?.alternateNames || []).join(', '),
    alias: profile?.alias || '',
    brand: profile?.brand || '',
    title: profile?.title || '',
    headline: profile?.headline || '',
    summary: profile?.summary || '',
    availability: profile?.availability || '',
    email: profile?.email || '',
    github: profile?.github || '',
    siteUrl: profile?.siteUrl || '',
    portfolio: profile?.portfolio || '',
    seoTitle: profile?.seo?.title || '',
    seoDescription: profile?.seo?.description || '',
    seoKeywordsInput: (profile?.seo?.keywords || []).join(', '),
    socials: {
      linkedin: profile?.socials?.linkedin || '',
      youtube: profile?.socials?.youtube || '',
      tiktok: profile?.socials?.tiktok || '',
      facebook: profile?.socials?.facebook || '',
    },
  };
}

function buildProfilePayload(profileForm) {
  return {
    name: profileForm.name,
    alternateNames: splitCommaField(profileForm.alternateNamesInput),
    alias: profileForm.alias,
    brand: profileForm.brand,
    title: profileForm.title,
    headline: profileForm.headline,
    summary: profileForm.summary,
    availability: profileForm.availability,
    email: profileForm.email,
    github: profileForm.github,
    siteUrl: profileForm.siteUrl,
    portfolio: profileForm.portfolio,
    socials: profileForm.socials,
    seo: {
      title: profileForm.seoTitle,
      description: profileForm.seoDescription,
      keywords: splitCommaField(profileForm.seoKeywordsInput),
    },
  };
}

function StudioPage({
  portfolio,
  loading,
  error,
  onRetry,
  onSaveProfile,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
}) {
  const location = useLocation();
  const [studioKey, setStudioKey] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [profileForm, setProfileForm] = useState(buildProfileForm());
  const [itemForm, setItemForm] = useState(createEmptyItemForm());
  const [busyAction, setBusyAction] = useState('');
  const [feedback, setFeedback] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useSiteSeo({
    portfolio,
    pathname: location.pathname,
    noIndex: true,
    title: portfolio
      ? `Backoffice | ${portfolio.profile.name} | ${portfolio.profile.brand}`
      : 'Backoffice',
  });

  useEffect(() => {
    const savedKey = window.localStorage.getItem(STUDIO_KEY_STORAGE);
    if (savedKey) {
      setStudioKey(savedKey);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STUDIO_KEY_STORAGE, studioKey);
  }, [studioKey]);

  useEffect(() => {
    if (!portfolio) {
      return;
    }

    setProfileForm(buildProfileForm(portfolio.profile));
    setItemForm((currentState) =>
      currentState.title || selectedItemId
        ? currentState
        : createEmptyItemForm(portfolio.taxonomies)
    );
  }, [portfolio, selectedItemId]);

  const stats = useMemo(() => {
    if (!portfolio) {
      return [];
    }

    return portfolio.taxonomies.statuses.map((status) => ({
      label: status,
      value: portfolio.items.filter((item) => item.status === status).length,
    }));
  }, [portfolio]);

  const handleProfileFieldChange = (event) => {
    const { name, value } = event.target;

    if (name.startsWith('socials.')) {
      const socialKey = name.split('.')[1];
      setProfileForm((current) => ({
        ...current,
        socials: {
          ...current.socials,
          [socialKey]: value,
        },
      }));
      return;
    }

    setProfileForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleItemChange = (event) => {
    const { name, value, type, checked } = event.target;

    setItemForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleArrayFieldChange = (field, value) => {
    setItemForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleLinkChange = (index, field, value) => {
    setItemForm((current) => ({
      ...current,
      links: current.links.map((link, linkIndex) =>
        linkIndex === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const handleVideoChange = (index, field, value) => {
    setItemForm((current) => ({
      ...current,
      videos: current.videos.map((video, videoIndex) =>
        videoIndex === index ? { ...video, [field]: value } : video
      ),
    }));
  };

  const resetItemForm = () => {
    setSelectedItemId('');
    setItemForm(createEmptyItemForm(portfolio?.taxonomies));
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setBusyAction('profile');
    setFeedback('');
    setErrorMessage('');

    try {
      const response = await onSaveProfile(buildProfilePayload(profileForm), studioKey);
      setFeedback(response.message || 'Profile saved.');
    } catch (submitError) {
      setErrorMessage(submitError.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleItemSubmit = async (event) => {
    event.preventDefault();
    setBusyAction('item');
    setFeedback('');
    setErrorMessage('');

    try {
      const payload = formToPayload(itemForm);
      const response = selectedItemId
        ? await onUpdateItem(selectedItemId, payload, studioKey)
        : await onCreateItem(payload, studioKey);

      setFeedback(response.message || 'Item saved.');
      resetItemForm();
    } catch (submitError) {
      setErrorMessage(submitError.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleDelete = async (itemId) => {
    setBusyAction(itemId);
    setFeedback('');
    setErrorMessage('');

    try {
      const response = await onDeleteItem(itemId, studioKey);
      setFeedback(response.message || 'Item removed.');

      if (selectedItemId === itemId) {
        resetItemForm();
      }
    } catch (deleteError) {
      setErrorMessage(deleteError.message);
    } finally {
      setBusyAction('');
    }
  };

  if (loading && !portfolio) {
    return (
      <div className="state-screen">
        <p className="state-screen__eyebrow">Studio</p>
        <h1>Loading studio workspace...</h1>
      </div>
    );
  }

  if (error && !portfolio) {
    return (
      <div className="state-screen">
        <p className="state-screen__eyebrow">Studio</p>
        <h1>The management backend is not responding.</h1>
        <p>{error}</p>
        <button type="button" className="primary-button" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="studio-page">
      <header className="studio-header">
        <div>
          <Link to="/" className="studio-backlink">
            <FaArrowLeft aria-hidden="true" />
            Back to portfolio
          </Link>
          <h1>Portfolio Backoffice</h1>
          <p>
            Manage projects, APK releases, open-source assets, IoT systems,
            blueprints, statuses, media, and social links from one page.
          </p>
        </div>

        <label className="studio-key-field">
          <span>
            <FaKey aria-hidden="true" />
            Studio access key
          </span>
          <input
            type="password"
            value={studioKey}
            onChange={(event) => setStudioKey(event.target.value)}
            placeholder={
              portfolio?.studio?.requiresKey
                ? 'Required on this server'
                : 'Optional in local development'
            }
          />
        </label>
      </header>

      <main className="studio-main">
        <section className="studio-overview">
          {stats.map((entry) => (
            <article key={entry.label} className="studio-stat">
              <strong>{entry.value}</strong>
              <span>{entry.label}</span>
            </article>
          ))}
        </section>

        {feedback ? (
          <div className="notice notice--success">
            <FaCheckCircle aria-hidden="true" />
            <span>{feedback}</span>
          </div>
        ) : null}

        {errorMessage ? <div className="notice notice--error">{errorMessage}</div> : null}

        <section className="studio-grid">
          <form className="studio-panel" onSubmit={handleProfileSubmit}>
            <div className="studio-panel__heading">
              <div>
                <span className="section-label">Profile</span>
                <h3>Profile and social links</h3>
              </div>
            </div>

            <div className="studio-form-grid">
              <label>
                <span>Name</span>
                <input name="name" value={profileForm.name} onChange={handleProfileFieldChange} />
              </label>
              <label>
                <span>Alternate names</span>
                <input
                  name="alternateNamesInput"
                  value={profileForm.alternateNamesInput}
                  onChange={handleProfileFieldChange}
                  placeholder="Nwachukwu Davis, another variation"
                />
              </label>
              <label>
                <span>Alias</span>
                <input
                  name="alias"
                  value={profileForm.alias}
                  onChange={handleProfileFieldChange}
                  placeholder="jacq_sparo"
                />
              </label>
              <label>
                <span>Brand</span>
                <input
                  name="brand"
                  value={profileForm.brand}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>Title</span>
                <input
                  name="title"
                  value={profileForm.title}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  name="email"
                  type="email"
                  value={profileForm.email}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>GitHub</span>
                <input
                  name="github"
                  value={profileForm.github}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>Site URL</span>
                <input
                  name="siteUrl"
                  value={profileForm.siteUrl}
                  onChange={handleProfileFieldChange}
                  placeholder="https://your-domain.com"
                />
              </label>
              <label>
                <span>Portfolio link</span>
                <input
                  name="portfolio"
                  value={profileForm.portfolio}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>LinkedIn</span>
                <input
                  name="socials.linkedin"
                  value={profileForm.socials.linkedin}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>YouTube</span>
                <input
                  name="socials.youtube"
                  value={profileForm.socials.youtube}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>TikTok</span>
                <input
                  name="socials.tiktok"
                  value={profileForm.socials.tiktok}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>Facebook</span>
                <input
                  name="socials.facebook"
                  value={profileForm.socials.facebook}
                  onChange={handleProfileFieldChange}
                />
              </label>
            </div>

            <div className="studio-form-grid">
              <label>
                <span>SEO title</span>
                <input
                  name="seoTitle"
                  value={profileForm.seoTitle}
                  onChange={handleProfileFieldChange}
                />
              </label>
              <label>
                <span>SEO keywords</span>
                <input
                  name="seoKeywordsInput"
                  value={profileForm.seoKeywordsInput}
                  onChange={handleProfileFieldChange}
                  placeholder="Davis Nwachukwu, jacq_sparo, Kyros Code"
                />
              </label>
            </div>

            <label>
              <span>Headline</span>
              <textarea
                name="headline"
                rows="3"
                value={profileForm.headline}
                onChange={handleProfileFieldChange}
              />
            </label>

            <label>
              <span>SEO description</span>
              <textarea
                name="seoDescription"
                rows="3"
                value={profileForm.seoDescription}
                onChange={handleProfileFieldChange}
              />
            </label>

            <label>
              <span>Summary</span>
              <textarea
                name="summary"
                rows="3"
                value={profileForm.summary}
                onChange={handleProfileFieldChange}
              />
            </label>

            <label>
              <span>Availability</span>
              <textarea
                name="availability"
                rows="2"
                value={profileForm.availability}
                onChange={handleProfileFieldChange}
              />
            </label>

            <div className="studio-actions">
              <button type="submit" className="studio-button" disabled={busyAction === 'profile'}>
                {busyAction === 'profile' ? 'Saving...' : 'Save profile'}
              </button>
            </div>
          </form>

          <StudioItemForm
            itemForm={itemForm}
            taxonomies={portfolio.taxonomies}
            onChange={handleItemChange}
            onArrayFieldChange={handleArrayFieldChange}
            onLinkChange={handleLinkChange}
            onVideoChange={handleVideoChange}
            onAddLink={() =>
              setItemForm((current) => ({
                ...current,
                links: [...current.links, createEmptyLink()],
              }))
            }
            onRemoveLink={(index) =>
              setItemForm((current) => ({
                ...current,
                links:
                  current.links.length === 1
                    ? [createEmptyLink()]
                    : current.links.filter((_, linkIndex) => linkIndex !== index),
              }))
            }
            onAddVideo={() =>
              setItemForm((current) => ({
                ...current,
                videos: [...current.videos, createEmptyVideo()],
              }))
            }
            onRemoveVideo={(index) =>
              setItemForm((current) => ({
                ...current,
                videos:
                  current.videos.length === 1
                    ? [createEmptyVideo()]
                    : current.videos.filter((_, videoIndex) => videoIndex !== index),
              }))
            }
            onSubmit={handleItemSubmit}
            onReset={resetItemForm}
            isEditing={Boolean(selectedItemId)}
            busy={busyAction === 'item'}
          />
        </section>

        <section className="content-section content-section--studio-list">
          <SectionHeader
            label="Content Inventory"
            title="Everything currently in the portfolio backend"
            description="Edit statuses when work is completed, move items into sale-ready states, or add new video, APK, repo, blueprint, and build-instruction content."
          />

          <div className="studio-stack">
            {portfolio.items.map((item) => (
              <article key={item.id} className="inventory-card">
                <div className="inventory-card__head">
                  <div>
                    <h3>{item.title}</h3>
                    <p>
                      {item.collection} • {item.category} • {item.status}
                    </p>
                  </div>
                  <div className="inventory-card__actions">
                    <button
                      type="button"
                      className="studio-inline-button"
                      onClick={() => {
                        setSelectedItemId(item.id);
                        setItemForm(itemToFormState(item));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      <FaEdit aria-hidden="true" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="studio-inline-button studio-inline-button--danger"
                      onClick={() => handleDelete(item.id)}
                      disabled={busyAction === item.id}
                    >
                      <FaTrashAlt aria-hidden="true" />
                      {busyAction === item.id ? 'Removing...' : 'Delete'}
                    </button>
                  </div>
                </div>

                <p>{item.summary}</p>

                <div className="chip-list">
                  {(item.technologies || []).map((technology) => (
                    <span key={`${item.id}-${technology}`}>{technology}</span>
                  ))}
                </div>

                <div className="inventory-card__meta">
                  <span>{item.links?.length || 0} links</span>
                  <span>{item.videos?.length || 0} videos</span>
                  <span>{item.featured ? 'Featured' : 'Standard listing'}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default StudioPage;
